// components/templates/sections/HERO_COFFEE.tsx
"use client";
import React from "react";
import { CoffeeBean } from "./CoffeeBean";
import { useDesign } from "@/context/DesignContext";

type HeroProps = {
  badge?: string;
  title?: string;
  subtitle?: string;
  origin?: string;
  roastLevel?: string;
  design?: any;
};

export default function HERO_COFFEE({
  badge = "◈ Est. 2009 · Single Origin",
  title = "Brewed\nfor the\nRitual.",
  subtitle = "Each cup is a deliberate act — sourced with precision, roasted with restraint, served with ceremony.",
  origin = "Ethiopia Yirgacheffe",
  roastLevel = "Light · 196°C",
  design: propDesign,
}: HeroProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const bgColor = design?.colors?.background || "#0C0500";
  const primaryColor = design?.colors?.primary || "#C8622A";
  const secondaryColor = design?.colors?.secondary || "#E8A45A";
  const textColor = design?.colors?.text || "#F5E6D3";
  const textLightColor = design?.colors?.textLight || "#BFA76A";
  const borderColor = design?.colors?.border || "#3D2A1A";

  // Gradient for hero background
  const heroBg = `radial-gradient(ellipse at 30% 60%, #1a0800 0%, ${bgColor} 70%)`;

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden"
             style={{ background: heroBg }}>
      {/* Background beans (static) – unchanged, but you could make bean colour dynamic if needed */}
      <div className="absolute inset-0 pointer-events-none">
        {[ 
          { x: "8%", y: "15%", s: 64, r: 25, d: 0 },
          { x: "82%", y: "10%", s: 40, r: -15, d: 1 },
          { x: "75%", y: "70%", s: 96, r: 45, d: 2 },
          { x: "15%", y: "80%", s: 48, r: -30, d: 3 },
          { x: "50%", y: "5%", s: 32, r: 60, d: 4 },
          { x: "90%", y: "45%", s: 56, r: -45, d: 0 },
        ].map((b, i) => (
          <div key={i} className="absolute drift" style={{ left: b.x, top: b.y, animationDelay: `${b.d}s` }}>
            <CoffeeBean size={b.s} opacity={0.08} rotate={b.r} />
          </div>
        ))}
      </div>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/3 w-[600px] h-[600px] rounded-full pointer-events-none"
           style={{ background: `radial-gradient(circle, ${primaryColor}26 0%, transparent 70%)` }} />
      <div className="absolute right-16 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full border rotate-slow pointer-events-none"
           style={{ borderColor: `${primaryColor}33`, borderStyle: "dashed" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pt-24">
        <div>
          <div className="font-mono-custom text-xs tracking-[0.5em] uppercase mb-8" style={{ color: primaryColor }}>
            {badge}
          </div>
          <h1 className="font-display text-7xl md:text-8xl font-black leading-none mb-6">
            <span className="text-gradient" style={{ backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Brewed
            </span>
            <br />
            <em style={{ color: textColor }}>for the</em>
            <br />
            <span className="text-gradient" style={{ backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Ritual.
            </span>
          </h1>
          <p className="text-xl leading-relaxed max-w-md mb-12"
             style={{ fontFamily: design?.typography?.bodyFont || "'Cormorant Garamond', serif", fontStyle: "italic", color: `${textLightColor}99` }}>
            {subtitle}
          </p>
          <div className="flex items-center gap-6">
            <button className="font-mono-custom text-sm tracking-widest uppercase px-10 py-4 font-medium transition-all duration-300 hover:scale-105"
                    style={{
                      backgroundColor: primaryColor,
                      color: bgColor,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = secondaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}>
              Explore Menu
            </button>
            <button className="font-mono-custom text-sm tracking-widest uppercase flex items-center gap-3 transition-colors duration-300"
                    style={{ color: `${primaryColor}B3` }}
                    onMouseEnter={(e) => e.currentTarget.style.color = secondaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = `${primaryColor}B3`}>
              <span className="w-10 h-px" style={{ backgroundColor: `${primaryColor}80` }} /> Our Story
            </button>
          </div>
        </div>

        {/* Cup visual – keep dynamic colors */}
        <div className="hidden lg:flex justify-center items-center relative">
          <div className="float relative">
            <Steam design={design} />
            <div className="relative mx-auto">
              <div className="w-52 h-52 rounded-full mx-auto flex items-center justify-center"
                   style={{
                     background: `radial-gradient(circle at 35% 35%, #3d1a08, ${bgColor})`,
                     boxShadow: `0 0 80px ${primaryColor}4D, inset 0 0 40px rgba(0,0,0,0.5)`
                   }}>
                <div className="w-36 h-36 rounded-full"
                     style={{
                       background: `radial-gradient(circle at 35% 35%, #4a2010, ${bgColor})`,
                       boxShadow: `inset 0 4px 20px ${primaryColor}33`
                     }}>
                  <svg viewBox="0 0 144 144" className="w-full h-full opacity-40">
                    <ellipse cx="72" cy="72" rx="52" ry="52" fill="none" stroke={primaryColor} strokeWidth="1" />
                    <path d="M72 30 Q90 55 72 72 Q54 89 72 114" stroke={secondaryColor} strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <path d="M40 55 Q55 65 72 72 Q89 79 108 89" stroke={primaryColor} strokeWidth="1" fill="none" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              <div className="w-64 h-8 mx-auto -mt-3 rounded-full"
                   style={{ background: `linear-gradient(180deg, #2d1407 0%, ${bgColor} 100%)`, boxShadow: "0 20px 60px rgba(0,0,0,0.8)" }} />
            </div>
            <div className="absolute -right-8 top-8 px-4 py-2 border backdrop-blur-sm"
                 style={{ borderColor: `${primaryColor}66`, background: `${bgColor}CC` }}>
              <div className="font-mono-custom text-xs tracking-widest" style={{ color: primaryColor }}>SINGLE ORIGIN</div>
              <div className="font-display text-sm font-bold" style={{ color: textColor }}>{origin}</div>
            </div>
            <div className="absolute -left-10 bottom-12 px-4 py-2 border backdrop-blur-sm"
                 style={{ borderColor: `${primaryColor}4D`, background: `${bgColor}CC` }}>
              <div className="font-mono-custom text-xs tracking-widest" style={{ color: primaryColor }}>ROAST LEVEL</div>
              <div className="font-display text-sm font-bold" style={{ color: textColor }}>{roastLevel}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <span className="font-mono-custom text-xs tracking-widest" style={{ color: `${primaryColor}80` }}>SCROLL</span>
        <div className="w-px h-12" style={{ background: `linear-gradient(to bottom, ${primaryColor}80, transparent)` }} />
      </div>
    </section>
  );
}

// Helper component – updated to use design
function Steam({ design }: { design?: any }) {
  const primaryColor = design?.colors?.primary || "#C8622A";
  const secondaryColor = design?.colors?.secondary || "#E8A45A";
  return (
    <div className="relative h-12 flex items-start justify-center">
      <svg viewBox="0 0 100 60" className="w-24 h-12 opacity-60">
        <path d="M20 40 Q20 20 30 10" stroke={secondaryColor} strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M50 40 Q50 20 60 10" stroke={secondaryColor} strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M80 40 Q80 20 90 10" stroke={secondaryColor} strokeWidth="2" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
}